import React, { useState } from "react";
import {
    Box,
    Paper,
    Typography,
    LinearProgress,
    Stack,
    Collapse,
    IconButton,
    Tooltip,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import type { InverterStatusViewModel } from "../../helpers/mapInverterOperationalStatus";

export type Notification = {
    type: "critical" | "performance" | "communication" | "generating" | "available";
    title: string;
    items: string[];
};

export type EquipmentsStatusProps = {
    online: number;
    offline: number;
    manutencao: number;
    performance: number;
    totalUnits: number;
    updatedAt: string;
    percent: number;
    icon: React.ReactNode;
    title: string;
    barColor?: string;
    notifications?: Notification[];
    operationalStatuses?: InverterStatusViewModel[];
    hidePerformance?: boolean;
    isTrafo?: boolean;
};

export const EquipmentsStatus: React.FC<EquipmentsStatusProps> = ({
    online,
    offline,
    manutencao,
    performance,
    totalUnits,
    updatedAt,
    percent,
    icon,
    title,
    barColor = "#fbc02d",
    notifications = [],
    operationalStatuses,
    hidePerformance,
    isTrafo,
}) => {
    const disponibilidade = Math.max(0, Math.min(100, percent));
    const [open, setOpen] = useState(false);

    const typeColors: Record<
        Notification["type"],
        { border: string; bg: string; titleColor: string }
    > = {
        critical: {
            border: "#d32f2f",
            bg: "rgba(211,47,47,0.15)",
            titleColor: "#f44336",
        },
        performance: {
            border: "#f9a825",
            bg: "rgba(249,168,37,0.15)",
            titleColor: "#ffb300",
        },
        communication: {
            border: "#6a1b9a",
            bg: "rgba(106,27,154,0.15)",
            titleColor: "#ba68c8",
        },
        generating: {
            border: "#2e7d32",
            bg: "rgba(46,125,50,0.15)",
            titleColor: "#66bb6a",
        },
        available: {
            border: "#0288d1",
            bg: "rgba(2,136,209,0.15)",
            titleColor: "#29b6f6",
        },
    };

    const renderLegacyCounters = () => (
        <Stack
            direction="row"
            spacing={4}
            justifyContent="center"
            alignItems="center"
            mt={1}
        >
            <Box textAlign="center">
                <Typography color="success.main" fontSize={12}>
                    ● Online
                </Typography>
                <Typography fontWeight="bold" color="success.main">
                    {online}
                </Typography>
            </Box>
            <Box textAlign="center">
                <Typography color="error.main" fontSize={12}>
                    ● Offline
                </Typography>
                <Typography fontWeight="bold" color="error.main">
                    {offline}
                </Typography>
            </Box>
            {!hidePerformance && (
                <Box textAlign="center">
                    <Typography color="warning.main" fontSize={12}>
                        ● Performance
                    </Typography>
                    <Typography fontWeight="bold" color="warning.main">
                        {performance}
                    </Typography>
                </Box>
            )}
            <Box textAlign="center">
                <Typography color="#ba68c8" fontSize={12}>
                    ● Falha de comunicação
                </Typography>
                <Typography fontWeight="bold" color="#ba68c8">
                    {manutencao}
                </Typography>
            </Box>
        </Stack>
    );

    const renderOperationalCounters = () => (
        <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            mt={1}
        >
            {operationalStatuses?.map((status) => (
                <Tooltip key={status.key} title={status.description} arrow>
                    <Box textAlign="center" sx={{ minWidth: 72 }}>
                        <Typography color={status.color} fontSize={12}>
                            ● {status.label}
                        </Typography>
                        <Typography fontWeight="bold" color={status.color}>
                            {status.total}
                        </Typography>
                    </Box>
                </Tooltip>
            ))}
        </Stack>
    );

    return (
        <Paper
            elevation={3}
            sx={{
                p: 2,
                borderRadius: "8px",
                width: "100%",
                backgroundColor: "transparent",
                backgroundImage: "none",
                boxShadow: "none",
                border: "1px solid #8F969E",
                marginBottom: "6px",
            }}
        >
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                    {icon}
                    <Box sx={{ ml: 1 }}>
                        <Typography variant="body2" fontWeight="bold" color="white">
                            {title}
                        </Typography>
                        <Typography variant="caption" fontSize={13} color="#8F969E">
                            {totalUnits} unidades • Atualizado {updatedAt}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        {!isTrafo && (
                            <Typography variant="body2" color={barColor} fontWeight="bold">
                                {disponibilidade.toFixed(1)}%
                            </Typography>)}
                        {notifications.length > 0 && (
                            <IconButton size="small" onClick={() => setOpen(!open)}>
                                {open ? (
                                    <ExpandLess fontSize="small" sx={{ color: "#8F969E" }} />
                                ) : (
                                    <ExpandMore fontSize="small" sx={{ color: "#8F969E" }} />
                                )}
                            </IconButton>
                        )}
                    </Box>
                    {!isTrafo && (
                        <Typography variant="caption" textAlign={"left"} fontSize={11} color="#8F969E">
                            Disponibilidade
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Contadores */}
            {!isTrafo &&
                (operationalStatuses
                    ? renderOperationalCounters()
                    : renderLegacyCounters())}

            {/* Barra de disponibilidade */}
            {!isTrafo && (<Box mt={1.5}>
                <LinearProgress
                    variant="determinate"
                    value={disponibilidade}
                    sx={{
                        height: 6,
                        borderRadius: 5,
                        bgcolor: "#444c5c",
                        "& .MuiLinearProgress-bar": {
                            bgcolor: barColor,
                        },
                    }}
                />
            </Box>)}

            {/* Collapse com notificações */}
            <Collapse in={open} timeout="auto" unmountOnExit>
                {notifications.map((n, idx) => (
                    <Box
                        key={idx}
                        mt={2}
                        border={`1px solid ${typeColors[n.type].border}`}
                        borderRadius={2}
                        bgcolor={typeColors[n.type].bg}
                        p={1.5}
                    >
                        <Typography
                            variant="body2"
                            sx={{ color: typeColors[n.type].titleColor, fontWeight: "600", width: "fit-content" }}
                        >
                            {n.title}
                        </Typography>
                        <Box sx={{ maxHeight: "250px", overflow: "auto" }}>
                            {n.items.length > 0 ? n.items.map((item, i) => (
                                <Typography
                                    key={i}
                                    variant="caption"
                                    color="white"
                                    sx={{ display: "block", ml: 1, width: "fit-content" }}
                                >
                                    • {item}
                                </Typography>
                            )) : (
                                <Typography
                                    variant="caption"
                                    color="white"
                                    sx={{ display: "block", ml: 1, width: "fit-content", textAlign: "center" }}
                                >
                                    Nenhum item encontrado
                                </Typography>
                            )}
                        </Box>
                    </Box>
                ))}
            </Collapse>
        </Paper>
    );
};
